import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const cities = [
  { name: 'Fayetteville', county: 'Cumberland' },
  { name: 'Hope Mills', county: 'Cumberland' },
  { name: 'Spring Lake', county: 'Cumberland' },
  { name: 'Lumberton', county: 'Robeson' },
  { name: 'Red Springs', county: 'Robeson' },
  { name: 'Dunn', county: 'Harnett' },
  { name: 'Lillington', county: 'Harnett' },
  { name: 'Coats', county: 'Harnett' },
  { name: 'Sanford', county: 'Lee' },
  { name: 'Benson', county: 'Johnston' },
  { name: 'Raleigh', county: 'Wake' },
  { name: 'Cary', county: 'Wake' },
  { name: 'Apex', county: 'Wake' },
  { name: 'Garner', county: 'Wake' },
  { name: 'Fuquay-Varina', county: 'Wake' },
  { name: 'Morrisville', county: 'Wake' },
  { name: 'Durham', county: 'Durham' },
  { name: 'Chapel Hill', county: 'Orange' },
  { name: 'Eastover', county: 'Cumberland' },
  { name: 'Stedman', county: 'Cumberland' },
  { name: 'Wade', county: 'Cumberland' },
  { name: 'Falcon', county: 'Cumberland' },
];

const counties = [
  'Cumberland', 'Robeson', 'Harnett', 'Johnston', 'Lee', 'Wake', 'Durham', 'Orange',
];

export default function ServiceAreaSection() {
  const { ref: titleRef, isVisible: titleVis } = useScrollAnimation({ threshold: 0.2 });
  const { ref: gridRef, isVisible: gridVis } = useScrollAnimation({ threshold: 0.05 });
  const { ref: ctaRef, isVisible: ctaVis } = useScrollAnimation({ threshold: 0.2 });

  return (
    <section className="py-28 bg-gradient-to-br from-[#F8F7FF] via-white to-[#FFFBF2] relative overflow-hidden">
      {/* Background accents */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-yellow/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-purple/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-screen-xl mx-auto px-4 sm:px-8">

        {/* Header */}
        <div ref={titleRef} className="text-center mb-16">
          <span className={`scroll-hidden ${titleVis ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-yellow/15 text-brand-yellow-dark font-body text-xs font-semibold uppercase tracking-widest border border-brand-yellow/30 mb-4`}>
            We Come to You
          </span>
          <h2 className={`scroll-hidden delay-200 ${titleVis ? 'scroll-visible' : ''} font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-brand-blue-dark mb-4 leading-tight`}>
            Serving Families Across<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D97706] to-[#7C3AED]">North Carolina</span>
          </h2>
          <p className={`scroll-hidden delay-300 ${titleVis ? 'scroll-visible' : ''} font-body text-gray-500 text-base max-w-xl mx-auto leading-relaxed`}>
            No studio required. Our instructors travel directly to your home across{' '}
            <strong className="text-brand-blue-dark">{counties.length} counties</strong> and{' '}
            <strong className="text-brand-blue-dark">{cities.length}+ cities</strong> throughout NC.
          </p>
        </div>

        {/* County pills */}
        <div className={`scroll-hidden ${titleVis ? 'scroll-visible' : ''} flex flex-wrap justify-center gap-2 mb-12`}>
          {counties.map((county) => (
            <span
              key={county}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-white border border-brand-purple/20 text-brand-purple font-body text-xs font-semibold"
            >
              <i className="ri-map-2-line text-xs" />
              {county} County
            </span>
          ))}
        </div>

        {/* City grid */}
        <div ref={gridRef} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-12">
          {cities.map((city, i) => (
            <div
              key={city.name}
              className={`scroll-hidden ${gridVis ? 'scroll-visible' : ''} group flex flex-col items-center text-center p-4 rounded-2xl bg-white border border-gray-100 hover:border-brand-yellow/50 hover:bg-gradient-to-br hover:from-[#FFFBEB] hover:to-white transition-all duration-300 cursor-default`}
              style={{ transitionDelay: `${(i % 12) * 40}ms` }}
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-brand-yellow/15 text-brand-yellow-dark mb-2 group-hover:bg-brand-yellow group-hover:text-brand-blue-dark transition-all duration-300 flex-shrink-0">
                <i className="ri-map-pin-2-line text-sm" />
              </div>
              <p className="font-heading text-brand-blue-dark text-xs font-black leading-snug">{city.name}</p>
              <p className="font-body text-gray-400 text-[10px] mt-0.5">{city.county} Co.</p>
            </div>
          ))}

          {/* "And more" tile */}
          <div className={`scroll-hidden ${gridVis ? 'scroll-visible' : ''} flex flex-col items-center text-center p-4 rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] border border-brand-purple/30 cursor-default`} style={{ transitionDelay: `${(cities.length % 12) * 40}ms` }}>
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/20 text-brand-yellow mb-2 flex-shrink-0">
              <i className="ri-add-line text-sm" />
            </div>
            <p className="font-heading text-white text-xs font-black leading-snug">And More</p>
            <p className="font-body text-white/60 text-[10px] mt-0.5">Always expanding</p>
          </div>
        </div>

        {/* Bottom CTA strip */}
        <div ref={ctaRef} className={`scroll-hidden ${ctaVis ? 'scroll-visible' : ''} flex flex-col sm:flex-row items-center justify-between gap-6 p-7 rounded-3xl bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6]`}>
          <div className="text-center sm:text-left">
            <p className="font-heading text-white text-xl font-black mb-1">Don&apos;t see your city?</p>
            <p className="font-body text-white/65 text-sm">Give us a call — we may already serve your area or can make arrangements.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <a
              href="tel:9102424567"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm cursor-pointer hover:bg-brand-yellow-light transition-all"
            >
              <i className="ri-phone-line" /> Call 910-242-4567
            </a>
            <Link
              to="/cities-we-serve"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-white/40 text-white font-body font-semibold text-sm cursor-pointer hover:border-brand-yellow hover:text-brand-yellow transition-all"
            >
              <i className="ri-map-2-line" /> View Full Service Area
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
